import React, { useState, useEffect, useCallback } from 'react';
import { Plus, GitBranch as GitBranchIcon, Edit2, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { apiFetch } from '../utils/api';
import ConfirmDialog from '../components/ConfirmDialog';

/** Parse Postgres DATE / ISO string without UTC shifting the calendar day. */
function parseLocalDate(raw) {
  if (raw == null || raw === '') return null;
  const s = String(raw);
  const m = s.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (m) {
    const y = Number(m[1]);
    const mo = Number(m[2]) - 1;
    const d = Number(m[3]);
    return new Date(y, mo, d);
  }
  const d = new Date(s);
  return Number.isNaN(d.getTime()) ? null : d;
}

function formatDecisionDate(raw) {
  const d = parseLocalDate(raw);
  if (!d) return 'Date unknown';
  return d.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });
}

/** 90-day reflection hint from decision_date (no reviewed column in DB). */
function reviewHint(decisionDateRaw) {
  const decided = parseLocalDate(decisionDateRaw);
  if (!decided) return { label: '', tone: 'muted' };
  const review = new Date(decided);
  review.setDate(review.getDate() + 90);
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  review.setHours(0, 0, 0, 0);
  const diffMs = review.getTime() - today.getTime();
  const days = Math.ceil(diffMs / (1000 * 60 * 60 * 24));
  if (days < 0) {
    return { label: '90-day check-in overdue', tone: 'due' };
  }
  if (days === 0) {
    return { label: '90-day check-in today', tone: 'soon' };
  }
  if (days <= 30) {
    return { label: `Check-in in ${days}d`, tone: 'soon' };
  }
  return { label: `Check-in in ${days}d`, tone: 'later' };
}

const reviewToneClass = {
  due: 'bg-error-light text-error',
  soon: 'bg-amber-light text-amber',
  later: 'bg-teal-light text-teal',
  muted: 'bg-bg-surface-alt text-text-tertiary',
};

const DecisionsPage = () => {
  const [decisions, setDecisions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalDecision, setModalDecision] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);

  const fetchDecisions = useCallback(async () => {
    try {
      const data = await apiFetch('/api/decisions');
      setDecisions(data.decisions || []);
    } catch (error) {
      console.error('Error fetching decisions:', error);
      toast.error(error?.message || 'Could not load decisions');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchDecisions();
  }, [fetchDecisions]);

  const performDelete = async () => {
    if (!deleteTarget) return;
    await apiFetch(`/api/decisions/${deleteTarget.id}`, { method: 'DELETE' });
    toast.success('Decision removed');
    fetchDecisions();
  };

  return (
    <div className="min-h-screen bg-bg-base p-6 lg:p-8" data-testid="decisions-page">
      <div className="max-w-5xl mx-auto">
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="font-display text-4xl font-bold text-text-primary mb-2">Decision Log</h1>
            <p className="text-text-secondary">Major choices, with a 90-day reflection rhythm</p>
          </div>
          <button
            type="button"
            onClick={() => setModalDecision('new')}
            className="flex items-center gap-2 px-6 py-3 bg-gold hover:bg-gold-muted text-white font-semibold rounded-xl transition-all shadow-sm"
            data-testid="new-decision-button"
          >
            <Plus size={20} />
            Log Decision
          </button>
        </div>

        {loading ? (
          <div className="text-center py-12">
            <div className="w-12 h-12 border-2 border-gold/30 border-t-gold rounded-full animate-spin mx-auto mb-4" />
          </div>
        ) : decisions.length === 0 ? (
          <div className="text-center py-12 bg-bg-surface rounded-2xl border border-border">
            <div className="w-16 h-16 bg-amber-light rounded-full flex items-center justify-center mx-auto mb-4">
              <GitBranchIcon className="text-amber" size={32} />
            </div>
            <h3 className="font-display text-xl font-bold text-text-primary mb-2">No decisions logged</h3>
            <p className="text-text-secondary mb-6">Start tracking your major choices</p>
            <button
              type="button"
              onClick={() => setModalDecision('new')}
              className="inline-flex items-center gap-2 px-6 py-3 bg-gold hover:bg-gold-muted text-white font-semibold rounded-xl transition-all"
            >
              <Plus size={20} />
              Log First Decision
            </button>
          </div>
        ) : (
          <div className="space-y-6">
            {decisions.map((decision) => {
              const hint = reviewHint(decision.decision_date);
              const title = decision.what_decided || 'Untitled decision';
              return (
                <div
                  key={decision.id}
                  className="bg-bg-surface rounded-2xl p-6 border border-border card-hover"
                >
                  <div className="flex items-start justify-between gap-3 mb-4">
                    <div className="min-w-0 flex-1">
                      <h3 className="font-display text-xl font-bold text-text-primary mb-2 break-words">
                        {title}
                      </h3>
                      <p className="text-sm text-text-tertiary">
                        Decided on {formatDecisionDate(decision.decision_date)}
                      </p>
                    </div>
                    <div className="flex flex-col items-end gap-2 shrink-0">
                      {hint.label ? (
                        <span
                          className={`px-3 py-1 rounded-full text-xs font-semibold whitespace-nowrap ${reviewToneClass[hint.tone]}`}
                        >
                          {hint.label}
                        </span>
                      ) : null}
                      <div className="flex items-center gap-1">
                        <button
                          type="button"
                          onClick={() => setModalDecision(decision)}
                          className="p-2 rounded-lg text-text-secondary hover:bg-gold-light hover:text-gold transition-colors"
                          aria-label="Edit decision"
                        >
                          <Edit2 size={18} />
                        </button>
                        <button
                          type="button"
                          onClick={() =>
                            setDeleteTarget({
                              id: decision.id,
                              preview: title,
                            })
                          }
                          className="p-2 rounded-lg text-text-secondary hover:bg-error-light hover:text-error transition-colors"
                          aria-label="Delete decision"
                        >
                          <Trash2 size={18} />
                        </button>
                      </div>
                    </div>
                  </div>

                  {decision.why_decided ? (
                    <div className="mb-3">
                      <p className="text-sm font-medium text-text-secondary mb-1">Why</p>
                      <p className="text-text-primary whitespace-pre-wrap">{decision.why_decided}</p>
                    </div>
                  ) : null}

                  {decision.outcome ? (
                    <div className="mb-3">
                      <p className="text-sm font-medium text-text-secondary mb-1">Expected outcome</p>
                      <p className="text-text-primary whitespace-pre-wrap">{decision.outcome}</p>
                    </div>
                  ) : null}
                </div>
              );
            })}
          </div>
        )}

        {modalDecision ? (
          <DecisionModal
            key={modalDecision === 'new' ? 'new' : modalDecision.id}
            decision={modalDecision === 'new' ? null : modalDecision}
            onClose={() => setModalDecision(null)}
            onSave={fetchDecisions}
          />
        ) : null}

        <ConfirmDialog
          open={!!deleteTarget}
          onClose={() => setDeleteTarget(null)}
          title="Delete this decision?"
          message="This will permanently remove it from your log."
          detail={deleteTarget?.preview}
          confirmLabel="Delete"
          onConfirm={performDelete}
        />
      </div>
    </div>
  );
};

/** Form uses API / DB field names (what_decided, decision_date, …). */
const DecisionModal = ({ decision, onClose, onSave }) => {
  const isEditing = Boolean(decision);
  const [formData, setFormData] = useState(() =>
    decision
      ? {
          what_decided: decision.what_decided || '',
          why_decided: decision.why_decided || '',
          outcome: decision.outcome || '',
          decision_date: decision.decision_date
            ? String(decision.decision_date).slice(0, 10)
            : new Date().toISOString().split('T')[0],
        }
      : {
          what_decided: '',
          why_decided: '',
          outcome: '',
          decision_date: new Date().toISOString().split('T')[0],
        }
  );
  const [saving, setSaving] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      if (isEditing) {
        await apiFetch(`/api/decisions/${decision.id}`, {
          method: 'PUT',
          body: JSON.stringify(formData),
        });
        toast.success('Decision updated');
      } else {
        await apiFetch('/api/decisions', {
          method: 'POST',
          body: JSON.stringify({
            what_decided: formData.what_decided,
            why_decided: formData.why_decided || null,
            outcome: formData.outcome || null,
            decision_date: formData.decision_date,
          }),
        });
        toast.success('Decision logged');
      }
      onSave();
      onClose();
    } catch (error) {
      console.error(error);
      toast.error(error?.message || 'Request failed');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/40 backdrop-blur-sm flex items-center justify-center z-50 p-4 animate-overlay-in" onClick={onClose}>
      <div
        className="bg-bg-surface rounded-2xl p-8 max-w-2xl w-full max-h-[90vh] overflow-y-auto animate-modal-in custom-scrollbar"
        onClick={(e) => e.stopPropagation()}
      >
        <h2 className="font-display text-3xl font-bold text-text-primary mb-6">
          {isEditing ? 'Edit decision' : 'Log a decision'}
        </h2>

        <form onSubmit={handleSubmit} className="space-y-6">
          <div>
            <label className="block text-sm font-medium text-text-primary mb-2">What did you decide?</label>
            <input
              type="text"
              value={formData.what_decided}
              onChange={(e) => setFormData({ ...formData, what_decided: e.target.value })}
              placeholder="e.g., Pivot to B2B"
              required
              className="w-full px-4 py-3 bg-bg-surface border border-border rounded-xl text-text-primary focus:border-gold focus:outline-none"
              data-testid="decision-input"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-text-primary mb-2">Why?</label>
            <textarea
              value={formData.why_decided}
              onChange={(e) => setFormData({ ...formData, why_decided: e.target.value })}
              placeholder="Your reasoning…"
              rows={3}
              className="w-full px-4 py-3 bg-bg-surface border border-border rounded-xl text-text-primary focus:border-gold focus:outline-none resize-none"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-text-primary mb-2">What do you expect to happen?</label>
            <textarea
              value={formData.outcome}
              onChange={(e) => setFormData({ ...formData, outcome: e.target.value })}
              placeholder="Expected outcome…"
              rows={3}
              className="w-full px-4 py-3 bg-bg-surface border border-border rounded-xl text-text-primary focus:border-gold focus:outline-none resize-none"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-text-primary mb-2">Decision date</label>
            <input
              type="date"
              value={formData.decision_date}
              onChange={(e) => setFormData({ ...formData, decision_date: e.target.value })}
              className="w-full px-4 py-3 bg-bg-surface border border-border rounded-xl text-text-primary focus:border-gold focus:outline-none"
            />
          </div>

          <div className="flex gap-4">
            <button
              type="button"
              onClick={onClose}
              className="px-6 py-3 border-2 border-border text-text-secondary rounded-xl hover:border-gold transition-all font-medium"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={saving || !formData.what_decided.trim()}
              className="flex-1 py-3 bg-gold hover:bg-gold-muted disabled:bg-text-tertiary/30 disabled:cursor-not-allowed text-white font-semibold rounded-xl transition-all"
            >
              {saving ? 'Saving…' : isEditing ? 'Save changes' : 'Log decision'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default DecisionsPage;
